import { createFeatureSelector, createSelector } from '@ngrx/store';

import type { ProjectDataState } from './project-data.state';
import type { ProjectState } from '../project.state';
import { selectQueryParams, selectRouteParams, selectUrl } from 'src/app/router.selectors';
import { ProductTreeTraverseService } from 'src/app/core/services/productTreeTraverse.service';
import type { Entity, TreeEntity } from '../../../core/models/entity.model';
import type { EntityType } from '../../../core/models/entity-type.model';
import { selectMeasurementUnits } from '../../../core/store/measurement-units/measurement-units.selector';
import type { MeasurementUnit } from '../../../core/models/measurement-unit.model';
import { selectEntityTypesState } from '../../../core/store/entity-types/entity-types.selectors';
import { MessagePropertyKey } from '../../../core/constants/message-property-keys';
import type { EntityNote } from '../../../core/models/entity-note';
import { MetadataKeys } from '../../../shared/constants/metadata-keys';
import { buildEntityTree } from '../../../core/entity-helpers/entity-tree-builder';
import { EntityTypeKey } from '../../../shared/constants/entity-type-keys';
import { selectMessageEntities } from 'src/app/core/store/messages/messages.selectors';
import { EntityTypesState } from 'src/app/core/store/entity-types/entity-types.state';

const selectProjectState = createFeatureSelector('project');

const selectProjectDataState = createSelector(selectProjectState, (projectState: ProjectState) => projectState.projectDataState);

export const selectCurrentProjectEntities = createSelector(selectProjectDataState, state => state.projectEntities);

export const selectTemporaryHoodlineEntities = createSelector(selectProjectDataState, state => state.temporaryHoodlineEntities);

export const selectProjectId = createSelector(selectRouteParams, (params): string | null => params?.['id']);

export const selectIsUserSettingPage = createSelector(selectUrl, url => {
  return url.toLowerCase().endsWith('user-settings');
});

export const selectProjectEntityWithoutParentsOrChildren = createSelector(
  selectCurrentProjectEntities,
  selectProjectId,
  (entities, projectId) => entities.find(e => e.Id === projectId)
);

export const selectProjectAirflowUnit = createSelector(
  selectProjectEntityWithoutParentsOrChildren,
  selectMeasurementUnits,
  (project, measurementUnits): MeasurementUnit | null => {
    if (project && measurementUnits) {
      return measurementUnits[project.Properties['MeasurementUnitAirflow'].Value as string];
    }
    return null;
  }
);

export const selectProjectCurrency = createSelector(
  selectProjectEntityWithoutParentsOrChildren,
  selectMeasurementUnits,
  (project, measurementUnits): MeasurementUnit | null => {
    if (project && measurementUnits) {
      return measurementUnits[project.Properties['MeasurementUnitCurrency'].Value as string];
    }
    return null;
  }
);

export const selectProjectWeightUnit = createSelector(
  selectProjectEntityWithoutParentsOrChildren,
  selectMeasurementUnits,
  (project, measurementUnits): MeasurementUnit | null => {
    if (project && measurementUnits) {
      return measurementUnits[project.Properties['Weight'].MeasurementUnit.Key as string];
    }
    return null;
  }
);

export const selectProjectEntityTree = createSelector(
  selectCurrentProjectEntities,
  selectProjectId,
  (projectEntities, projectId): TreeEntity => {
    if (!projectId || !projectEntities) {
      return null;
    }
    const entityTree = buildEntityTree(projectEntities);
    return entityTree[projectId];
  }
);

export const selectProjectFeedbackMessages = createSelector(
  selectProjectEntityTree,
  selectEntityTypesState,
  (project, entityTypesState): TreeEntity[] => getFeedbackMessages(project, entityTypesState)
);

export const selectProjectEntityNotes = createSelector(
  selectProjectEntityTree,
  selectEntityTypesState,
  (project, entityTypesState): EntityNote[] => {
    if (!entityTypesState.loaded || !project) {
      return [];
    }
    const entityTreeGetNotesRecursive = (entity: TreeEntity): EntityNote[] => {
      const type = entityTypesState.entityTypes[entity.EntityTypeKey];
      if (!type) {
        return [];
      }
      const properties = type.Groups.flatMap(g => g.Properties).filter(
        prop => prop.MetaData[MetadataKeys.propertyLevelInfo] && entity.Properties[prop.Key]?.Value
      );

      const notes: EntityNote[] = properties.map(property => ({ property: entity.Properties[property.Key], entity }));

      for (const child of entity.Children) {
        notes.push(...entityTreeGetNotesRecursive(child));
      }

      return notes;
    };

    return entityTreeGetNotesRecursive(project);
  }
);

export const selectCanDelete = (id: string) =>
  createSelector(selectProjectDataState, (state: ProjectDataState) => {
    const itemFound = state.projectActions.find(item => item.Id === id);
    return itemFound ? itemFound.CanDelete : false;
  });

export const selectAddableReferences = (parentId: string) =>
  createSelector(selectProjectDataState, (state: ProjectDataState) => {
    const item = state.projectActions.find(item => item.Id === parentId);
    return item ? item.AddableReferences : [];
  });

export const selectCanAdd = (parentId: string, productTypeId: string) =>
  createSelector(selectProjectDataState, (state: ProjectDataState) => {
    const item = state.projectActions.find(item => item.Id === parentId);
    const canAddIndex = item && item.AddableReferences.findIndex(ref => ref.ProductTypeId === productTypeId);
    return canAddIndex !== -1;
  });

export const selectCanAddInTheTree = (productTypeId: string) =>
  createSelector(selectProjectDataState, (state: ProjectDataState) => {
    let canAddInTheTree = false;
    state.projectActions.forEach(item => {
      item.AddableReferences.forEach(ref => {
        if (ref.ProductTypeId === productTypeId) {
          canAddInTheTree = true;
        }
      });
    });
    return canAddInTheTree;
  });

export const selectProjectEntityLoading = createSelector(selectProjectDataState, (state: ProjectDataState) => state.loading);

export const selectProjectLoaded = createSelector(selectProjectDataState, (state: ProjectDataState) => state.projectLoaded);

export const selectLatestActionCausedError = createSelector(
  selectProjectDataState,
  (state: ProjectDataState) => state.latestActionCausedError
);

export const selectLatestCreatedId = createSelector(selectProjectDataState, (state: ProjectDataState) => state.latestCreatedId);

export const selectLatestCopiedId = createSelector(selectProjectDataState, (state: ProjectDataState) => state.latestCopiedId);

export interface LatestCopyInformation {
  copiedId: string | null;
  parentId: string | null;
  projectId: string | null;
}

export const selectLatestCopyInformation = createSelector(selectProjectDataState, (state: ProjectDataState): LatestCopyInformation => {
  // Find the parent of the copied entity in the tree. We will use it to resolve the parent project id.
  const copiedEntityParent = state.projectEntities?.find(e => e.Id === state.latestCopiedEntityParentId);

  // If there's no parent, just return without project id
  if (!copiedEntityParent) {
    return {
      copiedId: state.latestCopiedId,
      parentId: state.latestCopiedEntityParentId,
      projectId: null
    };
  }

  // Simple parent traversing to find the project
  let parentProject = copiedEntityParent;
  while (parentProject?.ParentId && parentProject.EntityTypeKey !== EntityTypeKey.Project) {
    parentProject = state.projectEntities?.find(e => e.Id === parentProject.ParentId);
  }

  // Return with the project id
  return {
    copiedId: state.latestCopiedId,
    parentId: state.latestCopiedEntityParentId,
    projectId: parentProject?.Id
  };
});

export const selectLatestDeletedIdList = createSelector(selectProjectDataState, (state: ProjectDataState) => state.latestDeletedIdList);

export const selectSelectedId = createSelector(selectQueryParams, ({ selectedId }) => selectedId);

export const selectOpenedEditorEntityId = createSelector(selectSelectedId, selectProjectId, (selectedId, projectId) => {
  if (selectedId !== undefined) {
    return selectedId;
  }
  return projectId;
});

export const selectMessagesForOpenedEditorForProperty = (key: string, entityId: string) =>
  createSelector(
    selectProjectFeedbackMessages,
    selectMessageEntities,
    selectIsUserSettingPage,
    (projectMessages, otherMessages, isUserSettingsPage) => {
      if (!isUserSettingsPage) {
        return projectMessages.filter((message: Entity) => {
          return message.ParentId === entityId && (message.Properties['PropertyKey'].Value as string) === key;
        });
      } else {
        return otherMessages
          .filter((message: Entity) => {
            return message.ParentId === entityId && (message.Properties['PropertyKey'].Value as string) === key;
          })
          .map(x => x as TreeEntity);
      }
    }
  );

export const createMessagesForEntityPropertySelector = (entityId: string, propertyKey: string) => {
  return createSelector(selectProjectFeedbackMessages, messages => {
    return messages.filter((message: Entity) => {
      return message.ParentId === entityId && (message.Properties['PropertyKey'].Value as string) === propertyKey;
    });
  });
};

export const selectSelectedEntity = createSelector(
  selectProjectEntityTree,
  selectQueryParams,
  (projectEntity, { selectedId }): TreeEntity | null => {
    if (!projectEntity) {
      return null;
    }
    return ProductTreeTraverseService.findInTreeById(projectEntity, selectedId);
  }
);

export const selectHoodTypes = createSelector(selectEntityTypesState, (entityTypesState): EntityType[] => {
  if (!entityTypesState.loaded) {
    return [];
  }

  if (entityTypesState.entityTypes[EntityTypeKey.GastroFlowHood]) {
    return [entityTypesState.entityTypes[EntityTypeKey.GastroFlowHood]];
  }

  return [];
});

export const selectHoodTypeKeys = createSelector(selectHoodTypes, (state: EntityType[]) => {
  return state.map(type => type.Key) || [];
});

export const selectLastSelectedHoodId = createSelector(selectProjectDataState, (state: ProjectDataState) => state.lastSelectedHoodId);

export const selectLastSelectedModuleId = createSelector(selectProjectDataState, (state: ProjectDataState) => state.lastSelectedModuleId);

export const selectLastSelectedEntityId = createSelector(selectProjectDataState, (state: ProjectDataState) => state.lastSelectedEntityId);

export const selectTemporaryHoodlineEntityTree = createSelector(
  selectTemporaryHoodlineEntities,
  selectSelectedId,
  (tempEntities, selectedId): TreeEntity => {
    if (!selectedId || !tempEntities) {
      return null;
    }
    const entityTree = buildEntityTree(tempEntities);
    return entityTree[selectedId];
  }
);

export const selectTemporaryHoodlineFeedbackMessages = createSelector(
  selectTemporaryHoodlineEntityTree,
  selectEntityTypesState,
  (entityTree, entityTypesState): TreeEntity[] => getFeedbackMessages(entityTree, entityTypesState)
);

const getFeedbackMessages = (treeEntity: TreeEntity, entityTypesState: EntityTypesState): TreeEntity[] => {
  if (!entityTypesState.loaded || !treeEntity) {
    return [];
  }

  const recursiveGetFeedbackMessages = (entity: TreeEntity) => {
    let feedbackMessages: TreeEntity[] = [];

    if (entity.Children !== undefined && entity.Children.length > 0) {
      const children = [...entity.Children];

      const allPropertiesOrdered = entityTypesState.entityTypes[entity.EntityTypeKey]?.Groups.flatMap(group => group.Properties) || [];

      /*
       * Get the ordering index for messages: the index defines where the item is in the message list *before* we sort
       * the list by message level.
       * The index is the index of the property in the target entity. This makes the messages in the final list have the
       * same order as the properties in the entity, making the list behave consistently.
       */
      const messagesWithOrderIndices = children
        .filter(child => child.EntityTypeKey === EntityTypeKey.Message)
        .map(message => {
          const targetPropertyKey = message.Properties[MessagePropertyKey.PropertyKey]?.Value as string;
          if (!targetPropertyKey) {
            // No target property? Just use -1 as the index so the item goes to the beginning of the list.
            return { orderIndex: -1, message };
          }

          const orderIndex = allPropertiesOrdered.findIndex(prop => prop.Key === targetPropertyKey);
          return { orderIndex, message };
        });

      // Sort the messages for the current entity based on the order index
      messagesWithOrderIndices.sort((a, b) => {
        return a.orderIndex - b.orderIndex;
      });

      const messages = messagesWithOrderIndices.map(x => x.message);

      // Append the messages from this entity
      feedbackMessages = [...feedbackMessages, ...messages];

      // Recursively go through all children which aren't messages
      const nonMessageEntities = children.filter(child => child.EntityTypeKey !== EntityTypeKey.Message);
      if (nonMessageEntities.length > 0) {
        for (const child of nonMessageEntities) {
          const childMessages = recursiveGetFeedbackMessages(child);
          feedbackMessages = [...feedbackMessages, ...childMessages];
        }
      }
    }

    /* Messages are sorted by level from 0 (error) to 2 (info) */
    const sortBy = (a: Entity, b: Entity) => {
      return (a[MessagePropertyKey.Level] as number) - (b[MessagePropertyKey.Level] as number);
    };
    feedbackMessages.sort(sortBy);

    return feedbackMessages;
  };

  return recursiveGetFeedbackMessages(treeEntity);
};
